import { ErrorHandler, LocalizedPageTitle, LocalizedParagraph } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { LockedUsers } from '../Lockscreen/LockedUsers';
import { SignInCredentials } from './SignInCredentials/SignInCredentials';
import { TwoFactorAuthentication } from './TwoFactorAuthentication/TwoFactorAuthentication';
import { useSignInRouter } from './useSignInRouter';
import { SecurityInstructionsBanner } from './SecurityInstructionsBanner';

interface SignInProps {
    onSelectLockedUser: () => void;
}

export const SignIn = ({ onSelectLockedUser }: SignInProps) => {
    const {
        step,
        signInInfo,
        isCheckingPasskey,
        authenticationData,
        onEmailSubmit,
        onPasswordReset,
        onTwoFactorCheck,
        onPasskeyRetry,
    } = useSignInRouter();

    /**
     * Sadly, the email and credentials step need to be combined in a single component in order to have a consistent UX
     * If separated, the email input component would be a different one, resetting the cursor position
     */
    switch (step) {
        case 'email':
        case 'credentials':
            return (
                <SignInLayout>
                    <SignInCredentials
                        step={step}
                        signInInfo={signInInfo}
                        isCheckingPasskey={isCheckingPasskey}
                        onEmailSubmit={onEmailSubmit}
                        onPasswordReset={onPasswordReset}
                        onTwoFactorCheck={onTwoFactorCheck}
                        onPasskeyRetry={onPasskeyRetry}
                    />
                    <LockedUsers onSelectLockedUser={onSelectLockedUser} />
                </SignInLayout>
            );
        case '2fa':
            return authenticationData ? <TwoFactorAuthentication data={authenticationData} /> : <ErrorHandler />;
    }
};

function SignInLayout({ children }: React.PropsWithChildren<{}>) {
    return (
        <Stack spacing="500" vertical>
            <Stack spacing="200" vertical>
                <Stack spacing="100" vertical>
                    <LocalizedPageTitle textKey={TextKey.Welcome} />
                    <LocalizedParagraph textKey={TextKey.LoginInstructions} variant="secondary" />
                </Stack>
                <SecurityInstructionsBanner />
            </Stack>
            <Stack spacing="100" vertical>
                {children}
            </Stack>
        </Stack>
    );
}
